// ABOUTME: API client for the business-wide background-jobs read (#1983).
// GET /jobs returns snake_case; mapJob converts to the camelCase JobItem shape.

import { api } from './client'

export type JobStatus = 'queued' | 'running' | 'awaiting_input' | 'completed' | 'failed'

export interface JobItem {
  id: string
  source: string
  type: string
  title: string
  status: JobStatus
  rawStatus: string
  progress: number | null
  /**
   * The major stage a RUNNING job is in, in plain English ("Crawling your
   * site…"). Null once the job is terminal — the backend clears it so a
   * finished job can never read as still working.
   */
  stage: string | null
  errorMessage: string | null
  createdAt: string | null
  updatedAt: string | null
  startedAt: string | null
  completedAt: string | null
  entityType: string | null
  entityId: string | null
  deepLink: string | null
  /** Past its owning sweeper's timeout: it waits for the owner to retry or dismiss it. */
  stuck: boolean
  actions: JobAction[]
}

export type JobAction = 'retry' | 'dismiss'

interface BackendJob {
  id: string
  source: string
  type: string
  title: string
  status: string
  raw_status: string
  progress: number | null
  stage: string | null
  error_message: string | null
  created_at: string | null
  updated_at: string | null
  started_at: string | null
  completed_at: string | null
  entity_type: string | null
  entity_id: string | null
  deep_link: string | null
  stuck: boolean
  actions: JobAction[]
}

interface BackendJobsResponse {
  jobs: BackendJob[]
  active_count: number
}

export function mapJob(raw: BackendJob): JobItem {
  return {
    id: raw.id,
    source: raw.source,
    type: raw.type,
    title: raw.title,
    status: raw.status as JobStatus,
    rawStatus: raw.raw_status,
    progress: raw.progress,
    stage: raw.stage ?? null,
    errorMessage: raw.error_message,
    createdAt: raw.created_at,
    updatedAt: raw.updated_at,
    startedAt: raw.started_at,
    completedAt: raw.completed_at,
    entityType: raw.entity_type,
    entityId: raw.entity_id,
    deepLink: raw.deep_link,
    stuck: raw.stuck ?? false,
    actions: raw.actions ?? [],
  }
}

export const jobsApi = {
  /** Fetch the business's background jobs (active + recent terminal). */
  getJobs: async (): Promise<{ jobs: JobItem[]; activeCount: number }> => {
    const res = await api.get<BackendJobsResponse>('/jobs')
    const raw = res.data ?? (res as unknown as BackendJobsResponse)
    return {
      jobs: (raw.jobs ?? []).map(mapJob),
      activeCount: raw.active_count ?? 0,
    }
  },

  /** Retry or dismiss a stuck job (POST /jobs/{source}/{id}/settle). */
  settleJob: async (job: JobItem, action: JobAction): Promise<void> => {
    await api.post(`/jobs/${job.source}/${job.id}/settle`, { action })
  },
}
